import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Check, Copy } from 'lucide-react'
import { api } from '../../api/client'
import { useToast } from '../../components/ui/Toast'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/Dialog'
import { getApiErrorMessage } from '../../lib/api-errors'
import type { UserResponse } from '../../types'

interface ResetPasswordDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  user: UserResponse
}

export function ResetPasswordDialog({ open, onOpenChange, user }: ResetPasswordDialogProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [copied, setCopied] = useState(false)
  const name = user.fullName || user.email

  const mutation = useMutation({
    mutationFn: () => api.users.resetPassword(user.id),
    onError: (error) => {
      toast({ title: t('users.resetPassword.failed'), description: getApiErrorMessage(error), variant: 'error' })
    },
  })
  const temporaryPassword = mutation.data?.data.temporaryPassword

  async function copy(value: string) {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      toast({ title: t('users.resetPassword.copyFailed'), variant: 'error' })
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !mutation.isPending && onOpenChange(next)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{temporaryPassword ? t('users.resetPassword.doneTitle') : t('users.resetPassword.title')}</DialogTitle>
          <DialogDescription>
            {temporaryPassword
              ? t('users.resetPassword.doneDescription', { name })
              : t('users.resetPassword.description', { name })}
          </DialogDescription>
        </DialogHeader>

        {temporaryPassword && (
          <div className="flex gap-2">
            <input
              readOnly
              value={temporaryPassword}
              aria-label={t('users.resetPassword.passwordLabel')}
              className="w-full rounded-lg border border-input bg-background px-3 py-2 font-mono text-sm tracking-wider text-foreground"
            />
            <button
              type="button"
              onClick={() => copy(temporaryPassword)}
              className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-input px-3 py-2 text-sm font-medium text-foreground hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {copied ? <Check className="h-4 w-4" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
              {t('common.copy')}
            </button>
          </div>
        )}

        <DialogFooter>
          {temporaryPassword ? (
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
            >
              {t('common.close')}
            </button>
          ) : (
            <>
              <button
                type="button"
                className="rounded-md border border-input px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => onOpenChange(false)}
                disabled={mutation.isPending}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="rounded-md bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-destructive/60 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => mutation.mutate()}
                disabled={mutation.isPending}
              >
                {mutation.isPending ? t('users.resetPassword.pending') : t('users.resetPassword.confirm')}
              </button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
